// All character art, authored as ASCII in code. Templates use a small palette alphabet,
// and most are written as a left half which is mirrored: a very NES way of saving effort.

import { MONSTERS } from '../../shared/monsters';
import { HeroLook } from '../../shared/types';
import { encodeGear } from '../../shared/look';

export type Pal = Record<string, string>;

interface Template {
  rows: string[];
  mirror: boolean;
  pal: Pal;
}

const T = (rows: string[], pal: Pal, mirror = true): Template => ({ rows, mirror, pal });

const K: Pal = { k: '#000000', w: '#fcfcfc', r: '#f83800', y: '#f8d878' };

const TEMPLATES: Record<string, Template> = {
  slime: T(['........', '........', '........', '........', '........', '........', '.....kkk', '...kkbcc', '..kbbbcc', '.kbbbwwb', '.kbbbwkb', 'kabbbbbb', 'kabbbbbb', 'kaabbbbb', '.kaaaaab', '..kkkkkk'],
    { ...K, a: '#006800', b: '#00a800', c: '#58d854' }),
  wolf: T(['................', '................', '................', '............k.k.', '...........kbkbk', 'k.........kbbbbk', 'bk.......kbbbybk', 'kbk.kkkkkbbbbbcck', '.kbkbbbbbbbbbbkk.', '..kbbcccbbbbbk...', '..kbbbbbbbbbbk...', '..kabbbbbbbbak...', '..kakkaakkaak....', '..kak.kak.kak....', '..kk..kk..kk.....', '................'],
    { ...K, a: '#404040', b: '#7c7c7c', c: '#bcbcbc' }, false),
  shroom: T(['........', '........', '....kkkk', '..kkcccc', '.kcwcccc', '.kccccwc', 'kcccccwc', 'kbbbbbbb', '.kkkkkkk', '....keee', '....kekw', '....keee', '....keee', '...kedee', '...kkkkk', '........'],
    { ...K, b: '#a81000', c: '#f83800', d: '#c88038', e: '#fcd8a8' }),
  goblin: T(['........', '........', '........', '.k....kk', 'kbk..kbb', 'kbbkkbbb', '.kbbbbry', '..kbbbbb', '...kbkkk', '...kdddd', '..kbkddd', '..kbkddd', '...k.ddd', '....kakk', '....kak.', '....kkk.'],
    { ...K, a: '#503000', b: '#58d854', d: '#ac7c00' }),
  skeleton: T(['........', '.....kkk', '....kwww', '....kwww', '....kwkk', '....kwww', '.....kwk', '...kkkww', '..kwkwkk', '..kwkkww', '..kwkwkk', '..k.kwww', '.....kwk', '....kwkk', '....kwk.', '....kkk.'],
    { ...K, a: '#7c7c7c', b: '#bcbcbc', c: '#fcfcfc', d: '#f83800' }),
  archer: T(['.k......', 'kdk..kkk', 'kdk.kwww', 'kdk.kwww', 'kdk.kwkr', 'kdk.kwww', '.kd..kwk', '..kd.kww', '..kdwkkk', '.kdkkkww', '.kd.kwkk', 'kdk.kwww', 'kdk..kwk', 'kdk.kwkk', 'kk..kwk.', '....kkk.'],
    { ...K, d: '#ac7c00' }),
  zombie: T(['........', '.....kkk', '....kbbb', '....kbbb', '....kbkr', '....kbbb', '...kkkbk', '..kddddd', '.kbkdddd', '.kbkeddd', '.kbkdddd', '..k.dddd', '....kaak', '....kak.', '....kak.', '....kkk.'],
    { ...K, a: '#503000', b: '#88a858', d: '#6c5c48', e: '#887858' }),
  ghost: T(['........', '.....kkk', '...kkccc', '..kccccc', '..kcccck', '.kccccck', '.kcccccc', '.kcccccb', '.kcccccb', 'kbccccbb', 'kbbcccbb', 'kbbbbbbb', 'kbbbbbbb', 'kbkbbkbb', '.k.kk.kk', '........'],
    { ...K, b: '#7c7c9c', c: '#c8c8e8' }),
  necro: T(['........', '.....kkk', '....kaaa', '...kaabb', '...kabkk', '...kabkr', '...kaakk', '...kaaaa', '..kkbbbb', '.kbkbbcb', '.kbkbbbc', '.kwkbbbb', '..kkbbbb', '...kbbbb', '...kaaaa', '...kkkkk'],
    { ...K, a: '#2c0c40', b: '#50186c', c: '#9c3cc0' }),
  icemage: T(['.....kk.', '....kwck', '....kcbk', '...kaakk', '...kabkk', '...kabkw', '...kaakk', '...kaaaa', '..kkbbbb', '.kbkbbcb', '.kbkbbbc', '.kwkbbbb', '..kkbbbb', '...kbbbb', '...kaaaa', '...kkkkk'],
    { ...K, a: '#0058f8', b: '#3cbcfc', c: '#a4e4fc' }),
  scorpion: T(['.......k', '......kc', '......kb', '.......k', 'kk.....k', 'kbk...kb', 'kbk..kbb', '.kbkkbry', '..kbbbbb', '...kbbbb', '..kabbbb', '.kakaabb', '.k.kaaaa', '..kakkkk', '..kk....', '........'],
    { ...K, a: '#6c2800', b: '#a85010', c: '#f8a044' }),
  mummy: T(['........', '.....kkk', '....kcbc', '....kbcc', '....kckr', '....kcbc', '...kkkcb', '..kbcbcc', '.kcbkccb', '.kbckbcc', '.kcbkccb', '..k.kbcc', '....kcck', '....kbk.', '....kck.', '....kkk.'],
    { ...K, b: '#a89070', c: '#e8d8b0' }),
  wraith: T(['.....k..', '....kfk.', '...kfck.', '...kfcck', '..kfcccc', '..kccrcc', '..kccccc', '..kbcccb', '.kbbcccb', '.kbbbccb', '.kbbbbbb', '..kbbbbb', '..kbbkbb', '...kk.kb', '.......k', '........'],
    { ...K, b: '#a81000', c: '#f87800', f: '#f8d878' }),
  beetle: T(['........', '........', '........', '......kk', '.k...kaa', 'kak.kbcb', '.k.kbcbb', '..kbcbbb', '.kabbbbb', 'kakbbbbb', '.kabbbbb', '.kkabbbb', 'kak.kaaa', '.k...kkk', '........', '........'],
    { ...K, a: '#6c0c00', b: '#d82800', c: '#f8a044' }),
  drone: T(['........', '........', 'kk....kk', 'kek..kbb', '.kekkbcc', '..kbbccc', '..kbbcfw', '.kbbbccf', '.kabbbbb', '..kaaabb', '...kkaaa', '....k.kk', '...kfk..', '....k...', '........', '........'],
    { ...K, a: '#3c4458', b: '#7c8ca0', c: '#bcc8d8', e: '#bcbcbc', f: '#f83800' }),
  robot: T(['.......k', '......kf', '.....kkk', '....kbcc', '....kbkf', '....kbcc', '..kkkkkk', '.kabkbcc', 'kabbkbcc', 'kakbkbbc', 'kak.kbbb', 'kk..kaab', '....kaak', '...kbbk.', '...kaak.', '...kkkk.'],
    { ...K, a: '#585868', b: '#a0a0a8', c: '#d8d8e0', f: '#f8b800' }),
  turret: T(['........', '........', '.....kkk', '....kbcc', '...kbccd', '...kbcdw', '...kbbcc', '....kkkk', '......ka', '.....kaa', '....kaab', '...kaabb', '..kaaabb', '..kkkkkk', '........', '........'],
    { ...K, a: '#3c4458', b: '#7c8ca0', c: '#bcc8d8', d: '#f83800' }),
  xeno: T(['...k....', '..kak.kk', '..kakkaa', '...kaabb', '...kbbbc', '...kbbfb', '....kbbw', '..kkkbbk', '.kak.kbb', 'kak.kaab', '.k..kbba', '...kaabb', '..kak.ka', '.kak...k', '.kk.....', '........'],
    { ...K, a: '#141c28', b: '#2c3c50', c: '#58789c', f: '#58f898' }),
  spitter: T(['........', '.....kkk', '...kkfff', '..kfffee', '..kfeeee', '.kfeeefe', '.kfeeeee', '.kbkkkkk', '..kbbbby', '..kbbbbb', '...kbkkb', '..kbbbbb', '.kbkbbbb', '.kakkaab', '.kk..kkk', '........'],
    { ...K, a: '#141c28', b: '#2c3c50', e: '#b8f818', f: '#58a800' }),
  golem: T(['....kkkk', '...kbccc', '...kbcfb', '...kbccc', '.kkkkbbb', 'kbcckbbb', 'kbcckccb', 'kbbbkbcc', 'kbbkkbbb', 'kbbk.kab', '.kk..kab', '.....kab', '....kbbb', '...kbcbb', '...kaaab', '...kkkkk'],
    { ...K, a: '#402880', b: '#6844fc', c: '#b890fc', d: '#fc74b4', f: '#fc74b4' }),
  wisp: T(['........', '.....k..', '....kf..', '...kfck.', '...kcwck', '..kcwwwc', '..kcwwww', '..kcwwwc', '...kcwwc', '...kfcwc', '....kfcc', '.....kkf', '.......k', '........', '........', '........'],
    { ...K, c: '#f8d878', f: '#f87800' }),
  spider: T(['........', '........', 'k.......', 'bk.....k', '.bk...kb', '..bk.kbc', 'kk.kkbcc', 'bbkkbcry', '..kbbccc', '.kkkbbcc', 'kb.kkbbb', 'k.kb.kbb', '.kb...kk', '.k.....k', '........', '........'],
    { ...K, a: '#3c1858', b: '#5c2880', c: '#d8a8fc' }),
  construct: T(['.......k', '......kc', '.....kcb', '....kcbb', '...kcbbf', '..kcbbfw', '.kcbbbff', '.kabbbbb', '..kabbbb', '...kabbb', '....kabb', '.....kab', '......ka', '.......k', '........', '........'],
    { ...K, a: '#180c40', b: '#6844fc', c: '#d8b8f8', f: '#fc74b4' }),
  thug: T(['.......k', '......kf', '.....kkf', '....keee', '....kekk', '....keee', '.....kke', '...kkbbb', '..kebkbb', '..kebkbb', '..kkbkcb', '....kbbb', '....kaak', '....kak.', '....kak.', '....kkk.'],
    { ...K, a: '#202030', b: '#484860', c: '#f878f8', e: '#c88068', f: '#00e8d8' }),
  ninja: T(['........', '.....kkk', '....kbbb', '....kbbb', '....kwfk', '....kbbb', '...kkkkb', '..kbbbbb', '.kbkbbbb', '.kbkbbcb', '.kwkbbbb', '...kbbbb', '...kaabk', '...kab.k', '...kak..', '...kkk..'],
    { ...K, a: '#101018', b: '#28283c', c: '#00e8d8', f: '#00e8d8' }),
  hovercop: T(['........', '......kk', '.....kee', '....keew', '....kbbb', '...kbccc', '..kbbcfc', 'kkbbbccc', 'kbbbbbbb', 'kabbbbbb', '.kaaaaaa', '..kkkkkk', '...kf..k', '....k.kf', '.......k', '........'],
    { ...K, a: '#141c58', b: '#2038ec', c: '#6888fc', e: '#a4e4fc', f: '#f83800' }),
  cyborg: T(['.....kkk', '....keee', '....kekf', '....keee', '..kkkkee', '.kccbbbb', 'kcbkbbbb', 'kcbkbbcb', 'kbbkbbbb', 'kbbkbbbb', '.kk.kaaa', '....kaab', '....kaak', '...kbbk.', '...kaak.', '...kkkk.'],
    { ...K, a: '#202030', b: '#484860', c: '#bcbcbc', e: '#c88068', f: '#f83800' }),
  yeti: T(['...kkkkk', '..kcccww', '..kcckrw', '..kccwww', '.kccckkk', 'kcccwwww', 'kccccwww', 'kcckcwww', 'kcckcwww', 'kbbkcwww', '.kk.kccc', '....kcck', '....kcbk', '...kccbk', '...kbbbk', '...kkkk.'],
    { ...K, b: '#88b8d8', c: '#c8e8f8' }),
  imp: T(['........', '...k...k', '..kbk.kb', '..kbkkbb', '...kbbbb', '...kbbry', '....kbbb', '..kkkkkb', '.kbkbbbb', '.kbkbbbb', '..k.kbbb', '....kbbb', '...kbakb', '...kak.k', '...kk...', '........'],
    { ...K, a: '#6c0c00', b: '#d82800' }),
  demon: T(['.k......', 'kck....k', 'kck...kb', '.kck.kbb', '..kkkbbb', '...kbbry', '...kbbbb', '.kkkkbkk', 'kbbbkbbb', 'kbckkbbb', 'kbbkbbcb', 'kak.kbbb', '.k..kaab', '...kbbak', '...kaak.', '...kkk..'],
    { ...K, a: '#4c0838', b: '#8c1868', c: '#f8b800' }),
  eye: T(['........', '....kkkk', '..kkbbbb', '.kbbbccc', '.kbbcwww', 'kbbcwwww', 'kbbcwwrf', 'kbbcwwrk', 'kbbcwwrk', 'kbbcwwrf', '.kbbcwww', '.kbbbccc', '..kkbbbb', '...kkkkk', '..k.k.k.', '.k.k.k.k'],
    { ...K, b: '#6c1048', c: '#b02868', f: '#f8b800' }),
  warpling: T(['........', '........', '.....kkk', '....kfff', '...kfccc', '..kfcckw', '..kfcccc', '..kfcccf', '...kfffk', '...kkkk.', '...kb.kb', '..kb...k', '..k.....', '........', '........', '........'],
    { ...K, b: '#801890', c: '#d800cc', f: '#f878f8' }),

  // ─── Bosses (24px tall) ───
  b_treant: T(['..k....kk...', '.kak..kaak.k', '.kaak.kaakka', '..kaakkaaaka', '...kaabbbbaa', '..kbbbbbbcbb', '.kbbcbbbcccb', '.kbccbbccccb', '..kbbbbbbkkk', '...kkaaakrrf', '....kaaakrrk', '....kaaaakkk', '...kkaaaaaaa', '..kaaakaakka', '.kaakkaakkkk', 'kaak.kaaakka', 'kak..kaaaaaa', 'kk...kaaaaaa', '.....kaaaaaa', '....kaaakaaa', '...kaaak.kaa', '..kaaak..kaa', '..kkkk...kkk', '............'],
    { ...K, a: '#503000', b: '#006800', c: '#58d854', f: '#f8d878' }),
  b_lich: T(['......k.k.k.', '.....kfkfkfk', '.....kfffffk', '....kkkkkkkk', '....kwwwwwww', '...kwwwwwwww', '...kwwkkkwww', '...kwwkrkwww', '...kwwkkkwww', '....kwwwwkww', '.....kwkwkwk', '..kkkkkkkkkk', '.kbbbbkaaabb', 'kbccbbkaabbb', 'kbcbbkkaabbb', 'kbbk.kaabbbb', 'kwwk.kaabcbb', 'kwk..kaabbbb', '.k...kaabbbb', '.....kaabbbb', '....kaabbbbb', '....kaabbbbb', '...kaaabbbbb', '...kkkkkkkkk'],
    { ...K, a: '#180830', b: '#402080', c: '#9878f8', f: '#f8b800' }),
  b_djinn: T(['.......kkkkk', '......kfffff', '.....kffeeff', '.....keeeeee', '....kbbbbbbb', '....kbkwkbbb', '....kbbbbbbb', '.....kbbkkkk', '..kk..kkbbbb', '.kbbkkbbbbbb', 'kbbbbbbbcbbb', 'kbbkbbbccbbb', 'kbk.kbbbbbbb', '.k..kbbbbfff', '.kf.kfffffff', 'kbbk.kbbbbbb', 'kkk...kbbbcc', '.....kbbccca', '....kbcccaaa', '...kbccaaa..', '...kccaa....', '...kcaa.....', '....kak.....', '.....k......'],
    { ...K, a: '#a81000', b: '#3cbcfc', c: '#f87800', e: '#fcfcfc', f: '#f8b800' }),
  b_mech: T(['...kkk...kkk', '..kbcck.kbcc', '..kbbbkkkbcc', '..kkkkdddddd', '.....kdddeff', '.....kdddeew', 'kkkkkkdddddd', 'kbcbkbbbbkkk', 'kbbbkccbkcbb', 'kbfbkbbbkcbb', 'kbbbkbbbkbbb', 'kbcbkbbbkbfb', 'kbbbkkkkkfwf', 'kaaak.kbbbfb', 'kfff..kaaaaa', '.kk...kbbbbb', '....kkkbbaab', '...kbbbbk.ka', '...kbcbbk.ka', '...kbbbbk.kb', '...kaabbk.kb', '..kbbbbbbkkb', '..kaaaaaaakk', '..kkkkkkkkk.'],
    { ...K, a: '#3c4458', b: '#7c8ca0', c: '#bcc8d8', d: '#585868', e: '#f8b800', f: '#f83800' }),
  b_wyrm: T(['.k.......k..', 'kck.....kck.', 'kbck...kcbk.', '.kbckkkcbk..', '..kbbcccbbk.', '.kbbcccccbbk', '.kbcfbccbcfb', '.kbcffbcbcff', '..kbbccccccb', '...kbbkkkkbb', '....kkkwkwkk', '..kkkaaaaaaa', '.kbbbbcccccc', 'kbbcbbbccbcc', 'kbcccbbbcbcb', 'kbbcbkbbbcbb', '.kbbkkkbbbbc', '..kk..kbbbcc', '......kbbccb', '.....kbbcbbb', '....kbbcbbk.', '...kbbbbkk..', '...kaaak....', '...kkkk.....'],
    { ...K, a: '#402880', b: '#6844fc', c: '#d8b8f8', f: '#fc74b4' }),
  b_queen: T(['.......kkkkk', '......kfffff', '.....kfffeee', '.....kffewee', '.....kffeekk', '.....kfeeeee', '......kfeeek', '.......kkeek', '..kk.kkkbbbb', '.kcck.kbcbcc', 'kcbbkkbbcbcc', 'kcbk.kbbbccb', 'kbk..kbbbbcc', 'kcb...kbbbbb', 'kcb...kaaaaa', 'kck...kbbbbb', 'kck..kbbbcbb', 'kc...kbbcbbb', 'k...kbbbcbbb', '....kbbkkbbb', '....kbk..kbb', '....kbk..kbb', '...kaak..kaa', '...kkkk..kkk'],
    { ...K, a: '#303048', b: '#808098', c: '#e0e0f0', e: '#f8c8b8', f: '#f878f8' }),
  b_titan: T(['....kkkkkkkk', '...kcccwwwww', '..kcccwwwwww', '..kcckkkwwww', '..kcckfkwwww', '..kcckkkwwww', '..kccwwwwkkk', '...kbbkkkkww', '.kkkkbbbbbbb', 'kcwwkccwwwww', 'kcwwkccwwwww', 'kccwkcccwwww', 'kccwkcccwwww', 'kbccbkccccww', 'kbcck.kccccc', 'kcww..kbbccc', 'kcww..kbbbcc', '.kk...kbbbbb', '....kkkbbkkb', '...kcccbk.kc', '...kcccbk.kc', '...kbccbk.kb', '...kbbbbk.kb', '...kkkkkk.kk'],
    { ...K, b: '#5888b8', c: '#a4e4fc', f: '#3cbcfc' }),
  b_tyrant: T(['k..k...k...k', 'kf.kf..kf..k', 'kff.kf.kf.kf', '.kffkkffkkff', '..kfbbbbbbbb', '...kbbbbcccc', '..kbbbcccwww', '.kbbbccwwwww', '.kbbcwwwwrrr', 'kbbccwwwrrkk', 'kbbccwwwrrkk', 'kbbccwwwrrrr', '.kbbccwwwwww', '.kbbbcccwwww', '..kbbbbcccck', '...kbbbbbbbb', '..kakkbbbbkk', '.kaak.kakkak', '.kak.kaak.ka', 'kaak.kak.kaa', 'kak.kaak.kaa', 'kk..kak..kak', '....kk...kk.', '............'],
    { ...K, a: '#401058', b: '#801890', c: '#d800cc', f: '#f8b800' }),
};

const canvasCache = new Map<string, HTMLCanvasElement>();

function newCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function buildRows(t: Template): string[] {
  if (!t.mirror) return t.rows;
  return t.rows.map((r) => {
    const padded = r.padEnd(t.rows[0].length, '.');
    return padded + padded.split('').reverse().join('');
  });
}

type Frame = 'idle' | 'walk1' | 'walk2' | 'atk';

function renderTemplate(rows: string[], pal: Pal, frame: Frame): HTMLCanvasElement {
  const h = rows.length;
  const w = Math.max(...rows.map((r) => r.length));
  const c = newCanvas(w, h + 1);
  const ctx = c.getContext('2d')!;
  const legRows = Math.max(3, Math.floor(h / 5));
  const half = w >> 1;
  for (let y = 0; y < h; y++) {
    const row = rows[y];
    for (let x = 0; x < row.length; x++) {
      const ch = row[x];
      if (ch === '.') continue;
      const col = pal[ch];
      if (!col) continue;
      let dy = 0;
      // Walking lifts the legs on alternating sides; the upper body rocks.
      if (y >= h - legRows) {
        if (frame === 'walk1' && x < half) dy = -1;
        if (frame === 'walk2' && x >= half) dy = -1;
      } else if (frame === 'walk1' || frame === 'walk2') {
        dy = 0;
      }
      if (frame === 'atk' && y < h - legRows) dy = -1;
      ctx.fillStyle = col;
      ctx.fillRect(x, y + dy, 1, 1);
    }
  }
  return c;
}

function silhouette(src: HTMLCanvasElement, color: string): HTMLCanvasElement {
  const c = newCanvas(src.width, src.height);
  const ctx = c.getContext('2d')!;
  ctx.drawImage(src, 0, 0);
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, c.width, c.height);
  return c;
}

export interface SpriteSet {
  frames: Record<Frame, HTMLCanvasElement>;
  w: number;
  h: number;
}

const setCache = new Map<string, SpriteSet>();

function normalizePal(base: Pal, over?: Record<string, string>): Pal {
  return over ? { ...base, ...over } : base;
}

export function monsterSprite(defId: string): SpriteSet {
  let s = setCache.get(`m:${defId}`);
  if (s) return s;
  const def = MONSTERS[defId];
  const tName = def?.sprite ?? 'slime';
  const t = TEMPLATES[tName] ?? TEMPLATES.slime;
  const rows = buildRows(t);
  const pal = normalizePal(t.pal, def?.pal);
  const frames = {
    idle: renderTemplate(rows, pal, 'idle'),
    walk1: renderTemplate(rows, pal, 'walk1'),
    walk2: renderTemplate(rows, pal, 'walk2'),
    atk: renderTemplate(rows, pal, 'atk'),
  };
  s = { frames, w: frames.idle.width, h: frames.idle.height };
  setCache.set(`m:${defId}`, s);
  return s;
}

const flashCache = new Map<HTMLCanvasElement, Map<string, HTMLCanvasElement>>();
export function tinted(src: HTMLCanvasElement, color: string): HTMLCanvasElement {
  let m = flashCache.get(src);
  if (!m) {
    m = new Map();
    flashCache.set(src, m);
  }
  let c = m.get(color);
  if (!c) {
    c = silhouette(src, color);
    m.set(color, c);
  }
  return c;
}

/** Color-blended overlay version of a sprite, used for status effects like freeze. */
export function blended(src: HTMLCanvasElement, color: string, alpha: number): HTMLCanvasElement {
  const key = `${color}@${alpha}`;
  let m = flashCache.get(src);
  if (!m) {
    m = new Map();
    flashCache.set(src, m);
  }
  let c = m.get(key);
  if (!c) {
    c = newCanvas(src.width, src.height);
    const ctx = c.getContext('2d')!;
    ctx.drawImage(src, 0, 0);
    ctx.globalAlpha = alpha;
    ctx.drawImage(tinted(src, color), 0, 0);
    m.set(key, c);
  }
  return c;
}

// ─── Heroes: procedural paper-doll ───

function autoOutline(c: HTMLCanvasElement): void {
  const ctx = c.getContext('2d')!;
  const { width: w, height: h } = c;
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && d[(y * w + x) * 4 + 3] > 0;
  const out: [number, number][] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (solid(x, y)) continue;
      if (solid(x - 1, y) || solid(x + 1, y) || solid(x, y - 1) || solid(x, y + 1)) out.push([x, y]);
    }
  }
  ctx.fillStyle = '#000000';
  for (const [x, y] of out) ctx.fillRect(x, y, 1, 1);
}

function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, Math.max(0, Math.round(((n >> 16) & 255) * f)));
  const g = Math.min(255, Math.max(0, Math.round(((n >> 8) & 255) * f)));
  const b = Math.min(255, Math.max(0, Math.round((n & 255) * f)));
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

// Gear colors shared by every class. Rarity accents match the item name colors players know.
const GEAR_RARITY = ['#bcbcbc', '#6888fc', '#f8d878', '#f87858'];
const METAL = '#a0a0b0';
const METAL_L = '#d8d8e0';
const METAL_D = '#60606c';
const LEATHER = '#885020';
const LEATHER_D = '#503010';
const GOLD = '#f8b800';
const WARP = '#00e8d8';
const WARP_D = '#00a8a0';
const ASTRAL = '#b890fc';

/**
 * The hero paper doll, 16x18. The class fixes the silhouette, skin, hair and base colors; what the
 * hero wears (look.gear) restyles it. A look without gear is the class's designed default, used on
 * menus. `tick` alternates 0/1 a few times a second so that the top tiers can glow.
 */
// Heroes are drawn at twice the pixel density of the world: a 32x44 sprite that occupies the
// same 16x22 logical footprint. `blitHero` places it so that the figure lands exactly where the
// old 16x18 sprite stood, with extra headroom above for tall hats. Shapes and shading stay on
// the world's two-pixel grid, and the finer pixels go on rounding, faces, trim and glow, so a
// hero reads as the same art style as the monsters beside it. The outline is two pixels, the
// same weight as everything else.
export const HERO_W = 32;
export const HERO_H = 44;
/** Hi-res rows above where the old sprite's top-left corner sat. */
const HERO_TOP = 8;

/** Draws a hero sprite with its old 16x18 top-left at logical (x, y); dx/dy nudge by hi-res pixels. */
export function blitHero(g: CanvasRenderingContext2D, spr: HTMLCanvasElement, x: number, y: number, dx = 0, dy = 0): void {
  g.save();
  g.scale(0.5, 0.5);
  g.drawImage(spr, x * 2 + dx, y * 2 - HERO_TOP + dy);
  g.restore();
}

function drawHero(look: HeroLook, dir: number, frame: number, tick = 0): HTMLCanvasElement {
  const c = newCanvas(HERO_W, HERO_H);
  const g = c.getContext('2d')!;
  const px = (x: number, y: number, w: number, h: number, col: string) => {
    g.fillStyle = col;
    g.fillRect(x, y, w, h);
  };
  const gear = look.gear;
  const aT = gear ? gear.armorTier : -2; // -2: unknown, draw the class default
  const aR = gear?.armorRarity ?? -1;
  // Bare heroes wear dull underclothes; the top tiers brighten.
  const armor = aT === -1 ? shade(look.armor, 0.62) : aT === 0 ? shade(look.armor, 0.85) : aT >= 4 ? shade(look.armor, 1.12) : look.armor;
  const armorD = shade(armor, 0.68);
  const armorL = shade(armor, 1.3);
  const trim = aR >= 1 ? GEAR_RARITY[aR] : aT === -1 ? shade(look.trim, 0.7) : look.trim;
  const trimL = shade(trim, 1.2);
  const glow = tick ? WARP : WARP_D;
  const skin = look.skin;
  const skinD = shade(skin, 0.75);
  const hair = look.hair;
  const hairL = shade(hair, 1.3);
  const hairD = shade(hair, 0.7);
  const leg = shade(armor, 0.5);
  const side = dir >= 2;
  const back = dir === 1;
  const front = dir === 0;
  const y0 = 14;
  const robed = look.body === 'robe' || look.body === 'cloak';
  const gT = gear?.glovesTier ?? -1;
  const bT = gear?.bootsTier ?? -1;
  const hT = gear?.helmTier ?? -1;
  const hR = gear?.helmRarity ?? -1;
  const hAccent = hR >= 1 ? GEAR_RARITY[hR] : look.trim;
  const hand = gT < 0 ? skin : gT <= 1 ? LEATHER : gT <= 3 ? METAL : tick ? WARP : METAL;
  const handH = gT >= 2 ? 4 : 2;
  const boot = bT < 0 ? '#301c0c' : bT === 0 ? shade(skin, 0.8) : bT === 1 ? LEATHER : bT <= 3 ? METAL : '#2878a0';
  const bootH = bT >= 2 ? 4 : 2;
  const bootD = shade(boot, 0.7);

  // Legs
  if (!robed) {
    if (side) {
      const l1 = frame === 1 ? -2 : frame === 2 ? 2 : 0;
      px(12 + l1, y0 + 18, 4, 6, leg);
      px(12 + l1, y0 + 26 - bootH, 6, bootH, boot);
      px(12 + l1, y0 + 25, 6, 1, bootD);
      px(16 - l1, y0 + 18, 4, 6, shade(leg, 0.8));
      px(16 - l1, y0 + 26 - bootH, 6, bootH, shade(boot, 0.85));
      px(16 - l1, y0 + 25, 6, 1, bootD);
    } else {
      const lift1 = frame === 1 ? 2 : 0;
      const lift2 = frame === 2 ? 2 : 0;
      px(10, y0 + 18, 4, 6 - lift1, leg);
      px(10, y0 + 26 - bootH - lift1, 4, bootH, boot);
      px(10, y0 + 25 - lift1, 4, 1, bootD);
      px(18, y0 + 18, 4, 6 - lift2, leg);
      px(18, y0 + 26 - bootH - lift2, 4, bootH, boot);
      px(18, y0 + 25 - lift2, 4, 1, bootD);
    }
    if (bT >= 4 && tick) {
      // Warp treads leave a glowing spark underfoot.
      px(side ? 14 : 10, y0 + 26, 2, 1, WARP);
      px(side ? 18 : 20, y0 + 26, 2, 1, WARP);
    }
  }

  // Body
  const wide = look.body === 'heavy' ? 2 : 0;
  switch (look.body) {
    case 'robe':
    case 'cloak': {
      const sway = frame === 1 ? -2 : frame === 2 ? 2 : 0;
      px(10, y0 + 10, 12, 6, armor);
      px(8, y0 + 16, 16, 4, armor);
      px(8 + (sway < 0 ? -1 : 0), y0 + 20, 16 + Math.abs(sway), 4, armorD);
      px(6 + (sway < 0 ? 0 : 2), y0 + 24, 18, 2, armorD);
      if (!back) {
        px(14, y0 + 10, 4, 16, trim);
        px(15, y0 + 10, 2, 16, trimL);
      }
      if (look.body === 'cloak') {
        px(8, y0 + 10, 2, 14, armorD);
        px(22, y0 + 10, 2, 14, armorD);
      }
      px(10, y0 + 16, 12, 2, trim);
      // Robes show their boots below the hem.
      if (bT >= 0) {
        px(10, y0 + 26, 4, 2, boot);
        px(18, y0 + 26, 4, 2, boot);
      }
      break;
    }
    case 'heavy': {
      px(8, y0 + 10, 16, 10, armor);
      px(8, y0 + 10, 16, 2, armorL);
      px(10, y0 + 20, 12, 2, armorD);
      px(8, y0 + 18, 16, 2, armorD);
      if (!back) {
        px(14, y0 + 12, 4, 6, trim);
        px(15, y0 + 13, 2, 4, trimL);
      }
      break;
    }
    case 'coat': {
      px(10, y0 + 10, 12, 10, armor);
      px(10, y0 + 20, 12, 2, armorD);
      // Coat tails hang over the legs.
      px(9, y0 + 20, 3, 4, armorD);
      px(20, y0 + 20, 3, 4, armorD);
      if (!back) {
        px(14, y0 + 10, 4, 10, shade(armor, 0.55));
        px(10, y0 + 14, 12, 2, trim);
        px(15, y0 + 14, 2, 2, trimL);
      }
      break;
    }
    default: {
      px(10, y0 + 10, 12, 10, armor);
      px(10, y0 + 10, 12, 2, armorL);
      px(10, y0 + 17, 12, 1, armorD);
      px(10, y0 + 18, 12, 2, trim);
      if (!back) {
        px(15, y0 + 12, 2, 5, armorL);
        px(15, y0 + 18, 2, 2, trimL);
      }
      break;
    }
  }

  // Armor tier detailing over the body.
  if (aT === 1) {
    // Mail: a checker of lighter links across the chest.
    for (let y = y0 + 12; y <= y0 + 16; y += 2) for (let x = 10 + (((y - y0) >> 1) & 1) * 2; x <= 20; x += 4) px(x, y, 2, 2, armorL);
  } else if (aT >= 2 && !robed) {
    // Plate: a bright chest ridge, a seam between the plates, and a belt.
    px(10, y0 + 10, 12, 2, armorL);
    px(10, y0 + 14, 12, 1, armorD);
    if (!back) px(15, y0 + 12, 2, 6, armorL);
    px(10, y0 + 18, 12, 2, aT >= 3 ? trim : armorD);
    if (aT >= 3) {
      // Ornate: gold buckle and gilt edges.
      if (!back) px(15, y0 + 18, 2, 2, GOLD);
      px(10, y0 + 12, 1, 6, GOLD);
      px(21, y0 + 12, 1, 6, GOLD);
    }
  } else if (aT >= 2 && robed) {
    // Fine cloth: an embroidered hem, and trim down the sleeves from ornate up.
    px(8, y0 + 24, 16, 1, aT >= 3 ? trim : armorL);
    if (aT >= 3 && !back) {
      px(10, y0 + 20, 2, 2, trim);
      px(20, y0 + 20, 2, 2, trim);
    }
  }
  if (aT >= 4) {
    // Warpsteel: seams of light down the sides; astral adds a pulsing core.
    px(11, y0 + 12, 1, 6, glow);
    px(20, y0 + 12, 1, 6, glow);
    if (aT >= 5) {
      px(robed ? 8 : 10, y0 + 20, 2, 1, tick ? ASTRAL : WARP);
      px(robed ? 22 : 20, y0 + 20, 2, 1, tick ? WARP : ASTRAL);
      if (!back) {
        px(15, y0 + 14, 2, 2, tick ? '#fcfcfc' : ASTRAL);
        px(14, y0 + 15, 1, 1, ASTRAL);
        px(17, y0 + 14, 1, 1, ASTRAL);
      }
    }
  }

  // Arms
  const armSwing = frame === 1 ? 2 : frame === 2 ? -2 : 0;
  const shoulder = aT >= 4 ? (tick ? WARP : trim) : aT >= 3 ? trim : aT >= 2 ? armorL : look.body === 'heavy' ? trim : armorD;
  const sleeve = aT >= 2 && !robed ? shade(armor, 0.8) : armorD;
  if (side) {
    px(14 + armSwing, y0 + 12, 4, 6, sleeve);
    if (aT >= 2) px(14 + armSwing, y0 + 10, 4, 2, shoulder);
    px(14 + armSwing, y0 + 18, 4, handH, hand);
  } else {
    px(6 - wide, y0 + 10, 4 + wide, 4, shoulder);
    px(22, y0 + 10, 4 + wide, 4, shoulder);
    if (aT >= 3) {
      // Pauldrons rise above the shoulder line.
      px(6 - wide, y0 + 8, 4 + wide, 2, shoulder);
      px(22, y0 + 8, 4 + wide, 2, shoulder);
      px(7 - wide, y0 + 8, 2, 1, shade(shoulder, 1.3));
      px(23, y0 + 8, 2, 1, shade(shoulder, 1.3));
    }
    const lh = armSwing > 0 ? 2 : 0;
    const rh = armSwing < 0 ? 2 : 0;
    px(6, y0 + 14, 4, 4 + lh, sleeve);
    px(22, y0 + 14, 4, 4 + rh, sleeve);
    px(6, y0 + 18 + lh, 4, handH, hand);
    px(22, y0 + 18 + rh, 4, handH, hand);
  }

  // Head
  px(11, y0, 10, 1, skin);
  px(10, y0 + 1, 12, 8, skin);
  px(11, y0 + 9, 10, 1, skinD);
  px(10, y0 + 8, 1, 1, skinD);
  px(21, y0 + 8, 1, 1, skinD);
  // Neck
  px(14, y0 + 10, 4, 1, skinD);
  if (back) {
    px(11, y0 - 2, 10, 1, hair);
    px(10, y0 - 1, 12, 9, hair);
    px(12, y0, 2, 5, hairL);
    px(11, y0 + 7, 10, 1, hairD);
  } else if (side) {
    px(11, y0 - 2, 10, 1, hair);
    px(10, y0 - 1, 12, 3, hair);
    px(10, y0 + 2, 6, 4, hair);
    px(10, y0 + 6, 3, 2, hair);
    px(17, y0 + 2, 2, 1, hair);
    px(20, y0 + 2, 1, 1, hair);
    px(12, y0 - 1, 3, 1, hairL);
    // One eye, a nose in profile, and a mouth.
    px(18, y0 + 4, 2, 2, '#101018');
    px(22, y0 + 5, 1, 2, skin);
    px(18, y0 + 8, 2, 1, skinD);
  } else {
    px(11, y0 - 2, 10, 1, hair);
    px(10, y0 - 1, 12, 3, hair);
    px(10, y0 + 2, 2, 1, hair);
    px(13, y0 + 2, 2, 1, hair);
    px(17, y0 + 2, 2, 1, hair);
    px(20, y0 + 2, 2, 1, hair);
    px(10, y0 + 3, 1, 3, hair);
    px(21, y0 + 3, 1, 3, hair);
    px(12, y0 - 1, 4, 1, hairL);
    // Eyes and a mouth.
    px(13, y0 + 4, 2, 2, '#101018');
    px(17, y0 + 4, 2, 2, '#101018');
    px(15, y0 + 8, 2, 1, skinD);
  }

  // Headgear: a worn helm replaces the class's hat.
  if (hT >= 0) {
    switch (hT) {
      case 0: // Cap
        px(10, y0 - 2, 12, 1, LEATHER);
        px(9, y0 - 1, 14, 3, LEATHER);
        px(9, y0 + 1, 14, 1, LEATHER_D);
        if (!back) px(8, y0 + 2, 16, 1, LEATHER_D);
        break;
      case 1: // Skull cap
        px(10, y0 - 2, 12, 1, METAL_L);
        px(9, y0 - 1, 14, 2, METAL);
        px(8, y0 + 1, 16, 1, METAL_D);
        px(8, y0 + 2, 2, 4, METAL);
        px(22, y0 + 2, 2, 4, METAL);
        px(12, y0 - 1, 3, 1, METAL_L);
        break;
      case 2: // Great helm
        px(10, y0 - 3, 12, 1, METAL);
        px(9, y0 - 2, 14, 8, METAL);
        px(10, y0 - 2, 12, 1, METAL_L);
        px(9, y0 + 3, 14, 1, METAL_D);
        if (back) {
          px(9, y0 + 4, 14, 2, METAL_D);
        } else {
          px(side ? 16 : 11, y0 + 4, side ? 6 : 10, 2, '#000000');
          px(15, y0 - 6, 2, 1, hAccent);
          px(14, y0 - 5, 4, 3, hAccent);
        }
        break;
      case 3: // Crown
        px(9, y0 - 2, 14, 2, GOLD);
        px(10, y0 - 2, 3, 1, shade(GOLD, 1.3));
        px(8, y0 - 4, 2, 2, GOLD);
        px(12, y0 - 4, 2, 2, GOLD);
        px(18, y0 - 4, 2, 2, GOLD);
        px(22, y0 - 4, 2, 2, GOLD);
        px(14, y0 - 6, 4, 4, GOLD);
        px(15, y0 - 5, 2, 2, hAccent);
        if (!back) px(15, y0 - 2, 2, 2, hAccent);
        break;
      case 4: // Warp visor
        px(10, y0 - 2, 12, 1, METAL_L);
        px(9, y0 - 1, 14, 3, METAL);
        px(8, y0 + 2, 2, 4, METAL);
        px(22, y0 + 2, 2, 4, METAL);
        if (back) px(10, y0 + 2, 12, 2, METAL_D);
        else {
          px(side ? 14 : 10, y0 + 2, side ? 8 : 12, 4, glow);
          px(side ? 15 : 11, y0 + 3, side ? 6 : 10, 1, tick ? '#fcfcfc' : WARP);
        }
        break;
      default: {
        // Astral diadem: a band on the brow and a ring floating above it.
        px(9, y0, 14, 2, ASTRAL);
        if (!back) px(15, y0, 2, 2, hAccent);
        const ring = tick ? '#fcfcfc' : ASTRAL;
        px(10, y0 - 6, 12, 1, ring);
        px(8, y0 - 5, 2, 1, ring);
        px(22, y0 - 5, 2, 1, ring);
        px(tick ? 6 : 25, y0 - 6, 1, 1, '#fcfcfc');
        break;
      }
    }
  } else {
    switch (look.hat) {
      case 'hood':
        px(9, y0 - 3, 14, 1, armorD);
        px(8, y0 - 2, 16, 4, armorD);
        px(8, y0 + 2, 2, 8, armorD);
        px(22, y0 + 2, 2, 8, armorD);
        if (back) px(10, y0 + 2, 12, 8, armorD);
        else px(12, y0 + 4, 8, 1, trim);
        break;
      case 'helm':
        px(10, y0 - 3, 12, 1, armor);
        px(9, y0 - 2, 14, 8, armor);
        px(10, y0 - 2, 12, 1, armorL);
        px(9, y0 + 3, 14, 1, armorD);
        if (back) {
          px(9, y0 + 4, 14, 2, armorD);
        } else {
          px(side ? 16 : 11, y0 + 4, side ? 6 : 10, 2, '#000000');
          px(15, y0 - 6, 2, 1, trim);
          px(14, y0 - 5, 4, 3, trim);
        }
        break;
      case 'wizard':
        px(6, y0, 20, 2, armorD);
        px(10, y0 - 2, 12, 2, armor);
        px(12, y0 - 4, 8, 2, armor);
        px(14, y0 - 6, 4, 2, armor);
        px(16, y0 - 8, 2, 2, armorL);
        px(12, y0 - 4, 2, 2, armorL);
        px(12, y0 - 2, 8, 2, trim);
        break;
      case 'visor':
        px(10, y0 - 2, 12, 2, hair);
        if (back) px(10, y0 + 2, 12, 2, '#404040');
        else {
          px(10, y0 + 2, 12, 4, '#00e8d8');
        }
        if (side) px(10, y0 + 2, 6, 4, hair);
        break;
      case 'halo':
        px(10, y0 - 4, 12, 1, '#f8d878');
        px(8, y0 - 3, 2, 1, '#f8d878');
        px(22, y0 - 3, 2, 1, '#f8d878');
        px(12, y0 - 4, 2, 1, '#fcfcfc');
        break;
      case 'horns':
        px(8, y0 - 2, 2, 4, '#fcfcfc');
        px(22, y0 - 2, 2, 4, '#fcfcfc');
        px(7, y0 - 4, 1, 3, '#fcfcfc');
        px(24, y0 - 4, 1, 3, '#fcfcfc');
        break;
      default:
        break;
    }
  }

  // Off-hand: carried on the side away from the weapon.
  if (gear && gear.offhand) {
    const oT = gear.offTier;
    const oAccent = gear.offRarity >= 1 ? GEAR_RARITY[gear.offRarity] : look.trim;
    const ox = back ? 24 : 2;
    if (gear.offhand === 'shield') {
      if (oT <= 1) {
        // Buckler: wood with a metal boss.
        px(ox + 1, y0 + 12, 4, 1, LEATHER);
        px(ox, y0 + 13, 6, 6, LEATHER);
        px(ox + 1, y0 + 19, 4, 1, LEATHER);
        px(ox + 2, y0 + 14, 2, 4, oT === 0 ? METAL_D : METAL);
      } else if (oT <= 3) {
        // Kite shield with an emblem.
        px(ox, y0 + 10, 6, 12, METAL);
        px(ox, y0 + 10, 6, 2, METAL_L);
        px(ox + 1, y0 + 22, 4, 1, METAL);
        px(ox + 2, y0 + 23, 2, 1, METAL_D);
        px(ox + 2, y0 + 14, 2, 4, oAccent);
      } else {
        // Tower shield rimmed in warp light.
        px(ox, y0 + 8, 6, 16, METAL_D);
        px(ox + 1, y0 + 9, 4, 14, METAL);
        px(ox, y0 + 8, 6, 2, glow);
        px(ox, y0 + 22, 6, 2, glow);
        px(ox + 2, y0 + 14, 2, 4, tick ? oAccent : ASTRAL);
      }
    } else if (gear.offhand === 'orb') {
      const col = oT <= 1 ? '#3cbcfc' : oT <= 3 ? ASTRAL : WARP;
      const bx = back ? 24 : 4;
      px(bx + 1, y0 + 12, 2, 1, col);
      px(bx, y0 + 13, 4, 2, col);
      px(bx + 1, y0 + 13, 1, 1, '#fcfcfc');
      if (oT >= 2) {
        // Motes orbit the orb.
        px(bx + (tick ? -2 : 5), y0 + (tick ? 14 : 12), 1, 1, oT >= 4 ? '#fcfcfc' : col);
      }
    } else {
      // Ammunition on the belt, powered from the warp tier up.
      const bx = back ? 10 : 18;
      px(bx, y0 + 18, 6, 4, LEATHER_D);
      const bullet = oT >= 4 ? glow : oT >= 2 ? oAccent : GOLD;
      px(bx + 1, y0 + 18, 1, 2, bullet);
      px(bx + 3, y0 + 18, 1, 2, bullet);
      px(bx + 5, y0 + 18, 1, 2, bullet);
    }
  }

  autoOutline(c);
  autoOutline(c);
  if (dir === 2) {
    const f = newCanvas(HERO_W, HERO_H);
    const fx = f.getContext('2d')!;
    fx.scale(-1, 1);
    fx.drawImage(c, -HERO_W, 0);
    return f;
  }
  return c;
}

const heroCache = new Map<string, HTMLCanvasElement>();

export function heroFrame(look: HeroLook, dir: number, frame: number, tick = 0): HTMLCanvasElement {
  const gear = look.gear ? encodeGear(look.gear) : '-';
  // Only glowing gear changes with the tick; everything else shares one frame.
  const t = look.gear && (look.gear.armorTier >= 4 || look.gear.helmTier >= 4 || look.gear.glovesTier >= 4 || look.gear.bootsTier >= 4 || look.gear.offTier >= 2) ? tick : 0;
  const key = `${look.armor}|${look.body}|${look.hat}|${look.trim}|${look.hair}|${look.skin}|${gear}|${dir}|${frame}|${t}`;
  let c = heroCache.get(key);
  if (!c) {
    c = drawHero(look, dir, frame, t);
    heroCache.set(key, c);
  }
  return c;
}

// ─── Icons (items, weapons, skills) ───

const ICONS: Record<string, string[]> = {
  sword: ['.........kk.', '........kwck', '.......kwck.', '......kwck..', '.....kwck...', '.k..kwck....', '.kkkwck.....', '..kbkk......', '.kfkbk......', 'kfk..k......', 'kk..........', '............'],
  axe: ['....kkkk....', '...kwwcck...', '..kwccbkk...', '..kwcbk.k...', '..kcckk.kk..', '..kkk..kbk..', '......kbk...', '.....kbk....', '....kbk.....', '...kbk......', '..kfk.......', '..kk........'],
  mace: ['........kkk.', '.......kcwck', '......kcwwfk', '......kcccck', '.....kkkcck.', '....kbk.kk..', '...kbk......', '..kbk.......', '.kbk........', 'kfk.........', 'kk..........', '............'],
  hammer: ['......kkkk..', '.....kcwwck.', '....kccwcck.', '...kccccck..', '..kkcccck...', '.kbkkcck....', '.kbk.kk.....', 'kbk.........', 'kbk.........', 'kfk.........', 'kk..........', '............'],
  staff: ['.........kk.', '........kffk', '.......kfwfk', '........kfk.', '.......kbk..', '......kbk...', '.....kbk....', '....kbk.....', '...kbk......', '..kbk.......', '.kbk........', '.kk.........'],
  wand: ['..........kk', '.........kfk', '........kfwk', '.........kk.', '........kb..', '.......kb...', '......kb....', '.....kb.....', '....kb......', '...kf.......', '...k........', '............'],
  pistol: ['............', '............', '.kkkkkkkkkk.', 'kcwcccccccck', 'kbbbbbbbbbk.', '.kkkfkkkkk..', '..kbbkk.....', '..kbbk......', '.kbbk.......', '.kbbk.......', '.kkkk.......', '............'],
  rifle: ['............', '............', '...........k', 'kkkkkkkkkkkc', 'kbbkccccccck', 'kbbbbbfbkkk.', '.kbbbkk.k...', '..kkbk......', '....kk......', '............', '............', '............'],
  scepter: ['......kkk...', '.....kfwfk..', '.....kfffk..', '......kfk...', '.....kbbk...', '....kbk.....', '....kbk.....', '...kbk......', '...kbk......', '..kbk.......', '..kfk.......', '..kk........'],
  scythe: ['...kkkkkkk..', '..kwcccccck.', '.kk..kkkbkk.', '.......kbk..', '......kbk...', '......kbk...', '.....kbk....', '.....kbk....', '....kbk.....', '....kfk.....', '...kbk......', '...kk.......'],
  shield: ['.kkkkkkkkkk.', 'kcwwffffcccb', 'kcwfwwwfccck', 'kcfwwwwwfcck', 'kcfwwwwwfcck', 'kccfwwwfccck', 'kcccfwfccbbk', '.kcccfccbbk.', '.kbcccbbbbk.', '..kbbbbbbk..', '...kkbbkk...', '.....kk.....'],
  orb: ['....kkkk....', '..kkffffkk..', '.kffwwfffck.', '.kfwwffffck.', 'kffwfffffcck', 'kfffffffccck', 'kffffffcccck', '.kffffccccb.', '.kcfcccccbk.', '..kkcccckk..', '....kkkk....', '............'],
  mag: ['............', '.kkkkkkkkkk.', 'kcwcccccccck', 'kbfbfbfbfbbk', 'kbfbfbfbfbbk', 'kbfbfbfbfbbk', 'kbbbbbbbbbbk', '.kkkkkkkkkk.', '............', '............', '............', '............'],
  helm: ['....kkkk....', '..kkcwwckk..', '.kcwwcccccck', '.kcwccccccck', 'kcccfffffcck', 'kcckkkkkkcck', 'kcckkkkkkcck', 'kcccccccccck', 'kbcckckcccbk', '.kbckckccbk.', '..kbbbbbbk..', '...kkkkkk...'],
  armor: ['..kk....kk..', '.kcckkkkcck.', 'kcwwcfccwwck', 'kcwcfccccwck', 'kkcccffccckk', '.kkcfccfckk.', '..kcccccck..', '..kcffffck..', '..kcccccck..', '..kbbbbbbk..', '..kbbbbbbk..', '...kkkkkk...'],
  gloves: ['....kk.kk...', '...kcckcck..', '...kcckcckk.', '.kkkcccccck.', 'kcckcccccck.', 'kccccccccck.', '.kcccccccck.', '..kcccccck..', '..kffffffk..', '..kbbbbbbk..', '...kkkkkk...', '............'],
  boots: ['...kkkkk....', '...kcwcck...', '...kccck....', '...kccck....', '...kccck....', '...kffffk...', '...kcccckk..', '...kcccccck.', '...kcccccccb', '...kbbbbbbbk', '....kkkkkkk.', '............'],
  ring: ['....kkkk....', '...kfwwfk...', '...kffffk...', '...kkkkkk...', '..kcckkcck..', '.kcwk..kcck.', '.kck....kck.', '.kck....kbk.', '.kcck..kbbk.', '..kbbkkbbk..', '...kkkkkk...', '............'],
  amulet: ['.kk......kk.', '..kk....kk..', '...kk..kk...', '....kkkk....', '.....kk.....', '....kffk....', '...kfwwfk...', '...kfwffk...', '...kffffk...', '....kffk....', '.....kk.....', '............'],
  gold: ['............', '............', '.....kkk....', '....kfwfk...', '..kkkfffkkk.', '.kfwfkkkfwfk', '.kfffkkkfffk', '..kkkfwfkkk.', '....kfffk...', '.....kkk....', '............', '............'],
  potHp: ['.....kk.....', '....kbbk....', '.....kk.....', '....kwwk....', '...krrrrk...', '..krwrrrrk..', '..krwrrrrk..', '..krrrrrrk..', '..krrrrrrk..', '...kkkkkk...', '............', '............'],
  potMp: ['.....kk.....', '....kbbk....', '.....kk.....', '....kwwk....', '...kuuuuk...', '..kuwuuuuk..', '..kuwuuuuk..', '..kuuuuuuk..', '..kuuuuuuk..', '...kkkkkk...', '............', '............'],
};

const RARITY_ACCENT = ['#bcbcbc', '#6888fc', '#f8d878', '#f87858', '#f8d878', '#f83800'];

export function itemIcon(icon: string, rarity = 0): HTMLCanvasElement {
  const key = `i:${icon}:${rarity}`;
  let c = canvasCache.get(key);
  if (c) return c;
  const rows = ICONS[icon] ?? ICONS.ring;
  const accent = icon === 'gold' ? '#f8b800' : RARITY_ACCENT[rarity] ?? '#bcbcbc';
  const pal: Pal = { k: '#000000', w: '#fcfcfc', c: '#a0a0b0', b: '#885020', f: accent, r: '#d82800', u: '#2860f8' };
  c = newCanvas(12, 12);
  const g = c.getContext('2d')!;
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const col = pal[row[x]];
      if (col) {
        g.fillStyle = col;
        g.fillRect(x, y, 1, 1);
      }
    }
  });
  canvasCache.set(key, c);
  return c;
}

// ─── Held weapons ───
// Drawn to be held, not to sit in a bag, at the hero's double density, with a known grip
// pixel. Each is drawn once, pointing up-right ('diag') or right ('flat'), and only ever
// flipped or turned by right angles, so the pixels never smear.
// Letters: m metal, h metal highlight, d metal shadow, b wood, e dark grip, f accent (rarity or
// gold), g glow at the warp tiers, w white. The black outline is added automatically.

interface HeldTemplate {
  pose: 'diag' | 'flat';
  /** Pixel the hand closes around. */
  grip: [number, number];
  /** Grip to tip, in logical pixels: where a muzzle flash or casting motes go. */
  reach: number;
  rows: string[];
  /** The same weapon drawn pointing straight up, so that it can also face the four straight directions. */
  up?: { grip: [number, number]; rows: string[] };
}

const HELD: Record<string, HeldTemplate> = {
  sword: { pose: 'diag', grip: [8, 19], reach: 11, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '.....................hm.....',
    '....................hm......',
    '...................hmd......',
    '..................hmd.......',
    '.................hmd........',
    '................hmd.........',
    '...............hmd..........',
    '..............hmd...........',
    '.............hmd............',
    '........ff..hmd.............',
    '.........ffhmd..............',
    '..........ffd...............',
    '...........ff...............',
    '..........beff..............',
    '.........be..ff.............',
    '........be....ff............',
    '.......be...................',
    '.....ff.....................',
    '.....ff.....................',
  ], up: { grip: [13, 19], rows: [
    '............................',
    '............................',
    '.............h..............',
    '............hm..............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '............hmd.............',
    '.........fffffff............',
    '.........fffffff............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............ff..............',
    '............ff..............',
  ] } },
  axe: { pose: 'diag', grip: [8, 19], reach: 11, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '...................dmmh.....',
    '...................dmmmh....',
    '...................dmmmh....',
    '..................bdmmmhh...',
    '.................bedmmmhh...',
    '................be.dmmmhh...',
    '...............be..dmmmh....',
    '..............be...dmmh.....',
    '.............be.............',
    '............be..............',
    '...........be...............',
    '..........be................',
    '.........be.................',
    '........be..................',
    '.......be...................',
    '.....ff.....................',
    '.....ff.....................',
  ], up: { grip: [13, 19], rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '..............dmmh..........',
    '..............dmmmh.........',
    '..............dmmmh.........',
    '............bedmmmh.........',
    '............bedmmmh.........',
    '............bedmmmh.........',
    '............bedmmmh.........',
    '............bedmmh..........',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............ff..............',
    '............ff..............',
  ] } },
  mace: { pose: 'diag', grip: [8, 19], reach: 10, rows: [
    '............................',
    '............................',
    '............................',
    '....................f.......',
    '.................f.hhh.f....',
    '..................hmmmm.....',
    '..................mhmmmd....',
    '................f.mmmmmdf...',
    '..................mmmmmd....',
    '..................dmmmd.....',
    '.................feddd.f....',
    '................be..f.......',
    '...............be...........',
    '..............be............',
    '.............be.............',
    '............be..............',
    '...........be...............',
    '..........be................',
    '.........be.................',
    '........be..................',
    '.......be...................',
    '.....ff.....................',
    '.....ff.....................',
  ], up: { grip: [13, 19], rows: [
    '............................',
    '............................',
    '.............f..............',
    '..........f.hhh.f...........',
    '...........hmmmm............',
    '...........mhmmm............',
    '.........f.mmmmm.f..........',
    '...........mmmmm............',
    '...........mmmmd............',
    '..........f.ddd.f...........',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............ff..............',
    '............ff..............',
  ] } },
  hammer: { pose: 'diag', grip: [8, 19], reach: 9, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '.............dmmmh..........',
    '..............dmmmh.........',
    '...............dmmmh........',
    '................dmmmh.......',
    '.................dmmmh......',
    '.................bdmmmh.....',
    '................be.dmmmh....',
    '...............be...dmmmh...',
    '..............be.....dmmmh..',
    '.............be.............',
    '............be..............',
    '...........be...............',
    '..........be................',
    '.........be.................',
    '........be..................',
    '.......be...................',
    '.....ff.....................',
    '.....ff.....................',
  ], up: { grip: [13, 19], rows: [
    '............................',
    '............................',
    '............................',
    '.........dhhhhhhhh..........',
    '.........dmmmmmmmm..........',
    '.........dmmmmmmmm..........',
    '.........dmmmmmmmm..........',
    '.........dmmmmmmmm..........',
    '.........ddddddddd..........',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............ff..............',
    '............ff..............',
  ] } },
  staff: { pose: 'diag', grip: [8, 19], reach: 13, rows: [
    '............................',
    '............................',
    '........................g...',
    '....................g.f.....',
    '.....................fwff...',
    '.....................ffwf.g.',
    '.....................ffff...',
    '....................beff....',
    '...................be.......',
    '..................be........',
    '.................be.........',
    '................be..........',
    '...............be...........',
    '..............be............',
    '.............be.............',
    '............ff..............',
    '...........ff...............',
    '..........be................',
    '.........be.................',
    '........be..................',
    '.......be...................',
    '......be....................',
    '.....be.....................',
  ], up: { grip: [13, 19], rows: [
    '...............g............',
    '..........g..f..............',
    '............fwf.g...........',
    '...........fwfff............',
    '...........fffff............',
    '............fff.............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............ff..............',
    '............ff..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
  ] } },
  wand: { pose: 'diag', grip: [8, 19], reach: 8, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '..............g...g.........',
    '...............fwf..........',
    '...............fff..........',
    '...............fff.g........',
    '..............be............',
    '.............be.............',
    '............be..............',
    '...........be...............',
    '..........be................',
    '.........be.................',
    '........be..................',
    '.......be...................',
    '.....ff.....................',
    '.....ff.....................',
  ], up: { grip: [13, 19], rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '.............g..............',
    '............................',
    '...........g...g............',
    '............fwf.............',
    '............fff.............',
    '............fff.............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............ff..............',
    '............ff..............',
  ] } },
  scepter: { pose: 'diag', grip: [8, 19], reach: 10, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '................h...f...h...',
    '.................h.fff.h....',
    '..................ffwff.....',
    '...................fff......',
    '....................f.......',
    '............................',
    '.................md.........',
    '................md..........',
    '...............ff...........',
    '..............ff............',
    '.............md.............',
    '............md..............',
    '...........ff...............',
    '..........ff................',
    '.........md.................',
    '........md..................',
    '.......md...................',
    '.....ff.....................',
    '.....ff.....................',
  ], up: { grip: [13, 19], rows: [
    '............................',
    '............................',
    '.........h...f...h..........',
    '..........h.fff.h...........',
    '...........ffwff............',
    '............fff.............',
    '.............f..............',
    '............................',
    '............................',
    '............md..............',
    '............md..............',
    '............md..............',
    '............ff..............',
    '............ff..............',
    '............md..............',
    '............md..............',
    '............ff..............',
    '............ff..............',
    '............md..............',
    '............md..............',
    '............md..............',
    '............ff..............',
    '............ff..............',
  ] } },
  scythe: { pose: 'diag', grip: [8, 19], reach: 12, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '......................hhhh..',
    '.....................hmmmmh.',
    '.....................m....mh',
    '....................m.....mh',
    '...................be.....mh',
    '..................be......mh',
    '.................be.......d.',
    '................be.......d..',
    '...............be...........',
    '..............be............',
    '.............be.............',
    '............be..............',
    '...........be...............',
    '..........be................',
    '.........be.................',
    '........be..................',
    '.......be...................',
    '......be....................',
    '.....be.....................',
  ], up: { grip: [13, 19], rows: [
    '............................',
    '............................',
    '................hhhh........',
    '...............hmmmmh.......',
    '...............m....mh......',
    '..............m.....mh......',
    '............be......mh......',
    '............be......mh......',
    '............be......d.......',
    '............be.....d........',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
    '............be..............',
  ] } },
  pistol: { pose: 'flat', grip: [8, 17], reach: 7, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '......dhhhhhhhhhhhhhh.......',
    '......mmmmmmmmmmmmmmmmg.....',
    '......mmmmmmmmmmmmmmmmg.....',
    '......ddddddfffd............',
    '........eee.fff.............',
    '........eee.................',
    '.......eee..................',
    '.......eee..................',
    '......eee...................',
    '......eee...................',
  ] },
  rifle: { pose: 'flat', grip: [9, 16], reach: 10, rows: [
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '............................',
    '..............ff............',
    '..........hhhhhhhhhhhhhhhhh.',
    '....mmmmmmmmmmmmmmmmmmmmmmmg',
    '....mmmmmmmmmmmmmmmmmmmmmmmg',
    '..dddddddddddddddddd........',
    'bbbbdddddddddddddddd........',
    'bbbb....eee.dddd............',
    'bbb.....eee.dddd............',
    'bbb.....eee.dddd............',
    'bb.....eee..................',
    '.......eee..................',
  ] },
};

const HELD_SIZE = 32; // 28 of art plus a margin for the outline
const HELD_PAD = 2;
const HELD_METAL: [string, string, string][] = [
  ['#8c8c98', '#b4b4bc', '#585864'], // iron
  ['#a0a0b0', '#d8d8e0', '#60606c'], // steel
  ['#b8b8c8', '#fcfcfc', '#70707c'], // bright steel
  ['#c8c8d8', '#fcfcfc', '#787888'], // ornate
  ['#78c8d0', '#c8fcfc', '#3c8890'], // warp
  ['#b8a0e8', '#fcfcfc', '#705098'], // astral
];

export interface HeldSprite {
  c: HTMLCanvasElement;
  gx: number;
  gy: number;
  pose: 'diag' | 'flat' | 'up';
}

/** How a weapon is drawn at rest, or null for something that is never held. */
export function heldPose(icon: string): 'diag' | 'flat' | null {
  return HELD[icon]?.pose ?? null;
}

function heldPalette(tier: number, rarity: number): Pal {
  const lvl = tier < 0 ? 1 : Math.min(5, tier);
  const [m, h, d] = HELD_METAL[lvl];
  const f = rarity >= 1 ? GEAR_RARITY[Math.min(3, rarity)] : lvl >= 3 ? GOLD : '#bcbcbc';
  return {
    m, h, d, f,
    w: '#fcfcfc',
    b: lvl <= 1 ? LEATHER : lvl <= 3 ? '#6c3c18' : '#404058',
    e: lvl <= 3 ? LEATHER_D : '#282838',
    g: lvl >= 5 ? '#fcfcfc' : lvl >= 4 ? WARP : f,
  };
}

/** One drawing of a weapon: the main pose, or the upright one when `up` is set and exists. */
function heldBase(icon: string, tier: number, rarity: number, up: boolean): HeldSprite | null {
  const t = HELD[icon];
  if (!t) return null;
  const art = up ? t.up : null;
  if (up && !art) return null;
  const rows = art ? art.rows : t.rows;
  const grip = art ? art.grip : t.grip;
  const key = `hw:${icon}:${tier}:${rarity}:${up ? 'u' : 'm'}`;
  let c = canvasCache.get(key);
  if (!c) {
    const pal = heldPalette(tier, rarity);
    c = newCanvas(HELD_SIZE, HELD_SIZE);
    const g = c.getContext('2d')!;
    rows.forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        const col = pal[row[x]];
        if (col) {
          g.fillStyle = col;
          g.fillRect(x + HELD_PAD, y + HELD_PAD, 1, 1);
        }
      }
    });
    autoOutline(c);
    autoOutline(c);
    canvasCache.set(key, c);
  }
  return { c, gx: grip[0] + HELD_PAD, gy: grip[1] + HELD_PAD, pose: up ? 'up' : t.pose };
}

/** Grip to tip in logical pixels, for placing a muzzle flash or casting motes. */
export function heldReach(icon: string): number {
  return HELD[icon]?.reach ?? 8;
}

/** Flips a square sprite, or turns it a right angle clockwise. Both are exact. */
function turned(src: HTMLCanvasElement, flip: boolean): HTMLCanvasElement {
  const n = src.width;
  const c = newCanvas(n, n);
  const g = c.getContext('2d')!;
  if (flip) g.setTransform(-1, 0, 0, 1, n, 0);
  else g.setTransform(0, 1, -1, 0, n, 0);
  g.drawImage(src, 0, 0);
  return c;
}

/**
 * A held weapon pointing in direction `a`, in eighths of a turn clockwise from right (0 right,
 * 2 down, 4 left, 6 up). Diagonal directions come from the main drawing of a melee weapon and
 * straight ones from its upright drawing; guns only face straight. A direction a weapon cannot
 * make is snapped to the nearest one it can. `flip` mirrors the art for a hero facing the other
 * way. The sprite is at the hero's double density: draw it with `blitHeld`.
 */
export function heldWeapon(icon: string, tier: number, rarity: number, a: number, flip: boolean): HeldSprite | null {
  const t = HELD[icon];
  if (!t) return null;
  const wantUp = t.pose === 'diag' && (a & 1) === 0 && !!t.up;
  const base = heldBase(icon, tier, rarity, wantUp);
  if (!base) return null;
  let at = base.pose === 'diag' ? 7 : base.pose === 'up' ? 6 : 0;
  let gx = base.gx;
  let gy = base.gy;
  if (flip) {
    at = (4 - at + 8) % 8;
    gx = HELD_SIZE - 1 - gx;
  }
  const k = Math.round((((a - at) % 8) + 8) % 8 / 2) % 4;
  const key = `hwv:${icon}:${tier}:${rarity}:${base.pose}:${flip ? 1 : 0}:${k}`;
  let c = canvasCache.get(key);
  if (!c) {
    c = flip ? turned(base.c, true) : base.c;
    for (let i = 0; i < k; i++) c = turned(c, false);
    canvasCache.set(key, c);
  }
  for (let i = 0; i < k; i++) {
    const nx = HELD_SIZE - 1 - gy;
    gy = gx;
    gx = nx;
  }
  return { c, gx, gy, pose: base.pose };
}

/** Draws a held weapon with its grip at logical (hx, hy). */
export function blitHeld(g: CanvasRenderingContext2D, spr: HeldSprite, hx: number, hy: number): void {
  g.save();
  g.scale(0.5, 0.5);
  g.drawImage(spr.c, hx * 2 - spr.gx, hy * 2 - spr.gy);
  g.restore();
}

// 12x12 glyphs composed into skill buttons.
const SHAPES: Record<string, string[]> = {
  slash: ['..........w.', '.........wc.', '........wc..', '.......wc...', '......wc....', '.....wc.....', '....wc......', '..wwc.......', '..kw........', '.k.w........', 'k...........', '............'],
  flame: ['.....w......', '....wc......', '....wcc..w..', '...wccc.wc..', '..wccyccccw.', '..wcyyyccc..', '.wccyyyycc..', '.wcyyyyyccw.', '.wcyyyyyyc..', '..wcyyyycc..', '...wcyycc...', '....wccc....'],
  ice: ['.....ww.....', '....wccw....', '.w..wccw..w.', '..wwcccwww..', '..wccwwccw..', 'wwcwc..cwcww', 'wwcwc..cwcww', '..wccwwccw..', '..wwcccwww..', '.w..wccw..w.', '....wccw....', '.....ww.....'],
  bolt: ['......wyy...', '.....wyy....', '....wyy.....', '...wyy......', '..wyyyyyy...', '.....wyy....', '....wyy.....', '...wyy......', '..wyy.......', '..yy........', '.y..........', '............'],
  burst: ['.....ww.....', '.w...ww...w.', '..w..cc..w..', '...wcyycw...', '...cyyyyc...', 'wwcyyyyyycww', 'wwcyyyyyycww', '...cyyyyc...', '...wcyycw...', '..w..cc..w..', '.w...ww...w.', '.....ww.....'],
  shield: ['.wwwwwwwwww.', '.wccccccccw.', '.wccyyyyccw.', '.wccyyyyccw.', '.wccyyyyccw.', '.wcccyyccc w', '.wccccyyccw.', '..wccccccw..', '..wcccccw...', '...wcccw....', '....wcw.....', '.....w......'],
  skull: ['...wwwwww...', '..wccccccw..', '.wccccccccw.', '.wckkcckkcw.', '.wckkcckkcw.', '.wcccckcccw.', '..wcccccccw.', '...wckckcw..', '...wckckcw..', '....wwww....', '............', '............'],
  arrow: ['......ww....', '.......ww...', '.......wyw..', 'wwwwwwwwyyw.', 'wyyyyyyyyyyw', 'wwwwwwwwyyw.', '.......wyw..', '.......ww...', '......ww....', '............', '............', '............'],
  cross: ['....wwww....', '....wyyw....', '....wyyw....', 'wwwwwyywwwww', 'wyyyyyyyyyyw', 'wyyyyyyyyyyw', 'wwwwwyywwwww', '....wyyw....', '....wyyw....', '....wyyw....', '....wwww....', '............'],
  swirl: ['...wwwww....', '..wcccccw...', '.wcww..wcw..', 'wcw.....wc..', 'wc..www..w..', 'wc.wccw.....', 'wc.wc.w.....', 'wcw.www..w..', '.wcw....wc..', '..wcwwwwc...', '...wccccw...', '....wwww....'],
  grenade: ['.....kk.....', '....kwwk.k..', '....kyykk...', '...wwwwww...', '..wcckckcw..', '.wcckckckcw.', '.wckckckccw.', '.wcckckckcw.', '.wckckckccw.', '..wcckckcw..', '...wwwwww...', '............'],
  gun: ['............', '............', 'wwwwwwwwwww.', 'wcccccccccyw', 'wwwwwwwwwww.', '...wkcw.....', '..wcw.w.....', '.wccw.......', '.wcw........', '.www........', '............', '............'],
  rocket: ['..........ww', '........wwcw', '.......wccw.', '......wcyw..', '.....wcyw...', '..w.wcyw....', '..wwcyw.....', '...wcw......', '..yywww.....', '.yy..w......', 'y...........', '............'],
  mine: ['............', '............', '.....yy.....', '....wyyw....', '...wccccw...', '..wckcckcw..', '.wcccccccw..', 'wcckcckcccw.', 'wwwwwwwwwww.', '............', '............', '............'],
  turret: ['.....www....', '....wcccw...', 'wwwwwcycw...', 'wccccccw....', 'wwwwwccw....', '.....wcw....', '....wccw....', '...wccccw...', '..wccccccw..', '.wwwwwwwwww.', '............', '............'],
  hammer: ['..wwwwwwww..', '.wccccccccw.', '.wccccccccw.', '..wwwccwww..', '.....wyw....', '.....wyw....', '.....wyw....', '.....wyw....', '.....wyw....', '.....wyw....', '.....www....', '............'],
  wing: ['w...........', 'cw........w.', 'ccw......wc.', 'cccw....wcc.', '.ccyw..wycc.', '.ccyyw.wyyc.', '..cyyyyyyc..', '..ccyyyycc..', '...ccyycc...', '....cccc....', '.....cc.....', '............'],
  bone: ['.ww.......ww', 'wccw.....wcw', 'wcccwwwwwccw', '.wccccccccw.', 'wcccwwwwwccw', 'wccw.....wcw', '.ww.......ww', '............', '............', '............', '............', '............'],
  drop: ['.....ww.....', '.....ww.....', '....wccw....', '....wccw....', '...wccccw...', '..wcwccccw..', '.wcwcccccw..', '.wcwcccccw..', '.wcccccccw..', '..wcccccw...', '...wwwww....', '............'],
  eye: ['............', '...wwwwww...', '..wccccccw..', '.wccyyyyccw.', 'wcyykkkkyycw', 'wcyykkkkyycw', '.wccyyyyccw.', '..wccccccw..', '...wwwwww...', '............', '............', '............'],
  star: ['.....ww.....', '....wyyw....', '....wyyw....', 'wwwwyyyywwww', '.wyyyyyyyyw.', '..wyyyyyyw..', '...wyyyyw...', '..wyyyyyyw..', '..wyw..wyw..', '.wyw....wyw.', '.ww......ww.', '............'],
};

const SKILL_ICONS: Record<string, { s: string; bg: string; c: string; y: string }> = {
  cleave: { s: 'slash', bg: '#581010', c: '#bcbcbc', y: '#f8d878' },
  rend: { s: 'slash', bg: '#801010', c: '#f87858', y: '#f8d878' },
  leap: { s: 'arrow', bg: '#581010', c: '#fca044', y: '#f8d878' },
  warcry: { s: 'burst', bg: '#801010', c: '#f83800', y: '#f8d878' },
  whirl: { s: 'swirl', bg: '#581010', c: '#bcbcbc', y: '#f8d878' },
  bloodlust: { s: 'drop', bg: '#300808', c: '#f83800', y: '#f8d878' },
  earth: { s: 'burst', bg: '#503000', c: '#ac7c00', y: '#f8d878' },
  guillotine: { s: 'hammer', bg: '#801010', c: '#bcbcbc', y: '#885020' },
  firebolt: { s: 'flame', bg: '#581000', c: '#f83800', y: '#f8d878' },
  iceshard: { s: 'ice', bg: '#003058', c: '#3cbcfc', y: '#a4e4fc' },
  blink: { s: 'swirl', bg: '#2c1858', c: '#b890fc', y: '#fcfcfc' },
  nova: { s: 'burst', bg: '#003058', c: '#3cbcfc', y: '#fcfcfc' },
  chain: { s: 'bolt', bg: '#2c2c58', c: '#f8d878', y: '#fcfcfc' },
  shield: { s: 'shield', bg: '#2c1858', c: '#6888fc', y: '#b8b8f8' },
  meteor: { s: 'burst', bg: '#581000', c: '#f83800', y: '#f8b800' },
  void: { s: 'swirl', bg: '#0c0420', c: '#6844fc', y: '#d800cc' },
  blaster: { s: 'gun', bg: '#3c3000', c: '#bcbcbc', y: '#f8b800' },
  scatter: { s: 'gun', bg: '#583000', c: '#fca044', y: '#f8d878' },
  grenade: { s: 'grenade', bg: '#2c3c00', c: '#58a800', y: '#f8d878' },
  mine: { s: 'mine', bg: '#3c3000', c: '#7c7c7c', y: '#f83800' },
  turret: { s: 'turret', bg: '#3c3000', c: '#bcbcbc', y: '#f83800' },
  stim: { s: 'drop', bg: '#004830', c: '#58f898', y: '#fcfcfc' },
  rocket: { s: 'rocket', bg: '#581000', c: '#bcbcbc', y: '#f87800' },
  orbital: { s: 'star', bg: '#0c0420', c: '#fcfcfc', y: '#00e8d8' },
  holybolt: { s: 'cross', bg: '#584000', c: '#fcfcfc', y: '#f8d878' },
  heal: { s: 'cross', bg: '#004830', c: '#fcfcfc', y: '#58f898' },
  bless: { s: 'wing', bg: '#584000', c: '#fcfcfc', y: '#f8d878' },
  holynova: { s: 'burst', bg: '#584000', c: '#f8d878', y: '#fcfcfc' },
  sanctuary: { s: 'shield', bg: '#584000', c: '#f8d878', y: '#fcfcfc' },
  resurrect: { s: 'wing', bg: '#004830', c: '#58f898', y: '#fcfcfc' },
  hammer: { s: 'hammer', bg: '#584000', c: '#f8d878', y: '#fcfcfc' },
  ascend: { s: 'star', bg: '#584000', c: '#fcfcfc', y: '#f8d878' },
  bash: { s: 'shield', bg: '#003058', c: '#bcbcbc', y: '#3cbcfc' },
  taunt: { s: 'burst', bg: '#003058', c: '#f83800', y: '#f8d878' },
  ironskin: { s: 'shield', bg: '#303038', c: '#7c7c7c', y: '#fcfcfc' },
  charge: { s: 'arrow', bg: '#003058', c: '#3cbcfc', y: '#fcfcfc' },
  slam: { s: 'burst', bg: '#303038', c: '#bcbcbc', y: '#fcfcfc' },
  guardian: { s: 'wing', bg: '#003058', c: '#3cbcfc', y: '#fcfcfc' },
  spikes: { s: 'star', bg: '#303038', c: '#bcbcbc', y: '#f83800' },
  laststand: { s: 'skull', bg: '#801010', c: '#f8d878', y: '#fcfcfc' },
  bone: { s: 'bone', bg: '#2c1858', c: '#fcfcfc', y: '#58f898' },
  skeleton: { s: 'skull', bg: '#2c1858', c: '#fcfcfc', y: '#58f898' },
  curse: { s: 'eye', bg: '#2c1858', c: '#b890fc', y: '#d800cc' },
  poison: { s: 'drop', bg: '#203808', c: '#58d854', y: '#b8f818' },
  siphon: { s: 'swirl', bg: '#2c1858', c: '#d800cc', y: '#58f898' },
  corpse: { s: 'burst', bg: '#203808', c: '#58d854', y: '#bcbcbc' },
  golem: { s: 'skull', bg: '#303038', c: '#bcbcbc', y: '#58f898' },
  army: { s: 'skull', bg: '#0c0420', c: '#58f898', y: '#fcfcfc' },
};

export function skillIcon(icon: string): HTMLCanvasElement {
  const key = `s:${icon}`;
  let c = canvasCache.get(key);
  if (c) return c;
  const def = SKILL_ICONS[icon] ?? { s: 'star', bg: '#303038', c: '#bcbcbc', y: '#fcfcfc' };
  c = newCanvas(16, 16);
  const g = c.getContext('2d')!;
  g.fillStyle = def.bg;
  g.fillRect(0, 0, 16, 16);
  g.fillStyle = shade(def.bg, 1.8);
  g.fillRect(0, 0, 16, 1);
  g.fillRect(0, 0, 1, 16);
  g.fillStyle = shade(def.bg, 0.5);
  g.fillRect(0, 15, 16, 1);
  g.fillRect(15, 0, 1, 16);
  const rows = SHAPES[def.s] ?? SHAPES.star;
  const pal: Pal = { w: shade(def.c, 1.25), c: def.c, y: def.y, k: '#000000' };
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      const col = pal[row[x]];
      if (col) {
        g.fillStyle = col;
        g.fillRect(x + 2, y + 2, 1, 1);
      }
    }
  });
  canvasCache.set(key, c);
  return c;
}

export { shade };
